import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { NotificationCategory, NotificationPageView, NotificationView } from 'shared';
import { notificationApi } from '@/api/notification.api';
import type { RealtimeEvent } from '@/composables/useCollaborationSocket';
import { registerRealtimeEventHandler } from './realtimeDispatcher';

export const useNotificationStore = defineStore('notification', () => {
  const entities = ref<Record<string, NotificationView>>({});
  const loading = ref(false);
  const unreadCount = ref(0);
  const nextCursor = ref<string | null>(null);
  const pendingLoads = new Map<NotificationCategory | undefined, Promise<NotificationPageView>>();
  let requestVersion = 0;
  const items = computed(() =>
    Object.values(entities.value).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  );

  function upsert(item: NotificationView): void {
    entities.value[item.id] = item;
  }
  async function load(category?: NotificationCategory): Promise<void> {
    const version = ++requestVersion;
    loading.value = true;
    let pending = pendingLoads.get(category);
    try {
      if (!pending) {
        pending = notificationApi.list({ category });
        pendingLoads.set(category, pending);
      }
      const page = await pending;
      if (version !== requestVersion) return;
      entities.value = Object.fromEntries(page.items.map((item) => [item.id, item]));
      unreadCount.value = page.unreadCount;
      nextCursor.value = page.nextCursor;
    } finally {
      if (pendingLoads.get(category) === pending) pendingLoads.delete(category);
      if (version === requestVersion) loading.value = false;
    }
  }
  async function loadMore(category?: NotificationCategory): Promise<void> {
    if (!nextCursor.value || loading.value) return;
    const version = ++requestVersion;
    loading.value = true;
    try {
      const page = await notificationApi.list({ category, cursor: nextCursor.value });
      if (version !== requestVersion) return;
      page.items.forEach(upsert);
      unreadCount.value = page.unreadCount;
      nextCursor.value = page.nextCursor;
    } finally {
      if (version === requestVersion) loading.value = false;
    }
  }
  async function markRead(id: string): Promise<void> {
    const before = entities.value[id];
    upsert(await notificationApi.markRead(id));
    if (before && !before.readAt) unreadCount.value = Math.max(0, unreadCount.value - 1);
  }
  async function markAllRead(): Promise<void> {
    await notificationApi.markAllRead();
    const readAt = new Date().toISOString();
    Object.values(entities.value).forEach((item) => {
      if (!item.readAt) upsert({ ...item, readAt });
    });
    unreadCount.value = 0;
  }
  async function remove(id: string): Promise<void> {
    await notificationApi.remove(id);
    if (entities.value[id] && !entities.value[id].readAt)
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    delete entities.value[id];
  }
  async function clear(): Promise<void> {
    await notificationApi.clear();
    entities.value = {};
    unreadCount.value = 0;
    nextCursor.value = null;
  }
  function handleRealtimeEvent(event: RealtimeEvent): void {
    if (!['notification.created.v1', 'notification.updated.v1'].includes(event.type)) return;
    const item = event.payload as unknown as NotificationView;
    const previous = entities.value[item.id];
    upsert(item);
    if (!previous && !item.readAt) unreadCount.value += 1;
  }

  registerRealtimeEventHandler('notification.created.v1', handleRealtimeEvent);
  registerRealtimeEventHandler('notification.updated.v1', handleRealtimeEvent);
  return {
    entities,
    items,
    unreadCount,
    nextCursor,
    loading,
    load,
    loadMore,
    markRead,
    markAllRead,
    remove,
    clear,
    handleRealtimeEvent,
  };
});
